/**
 * Startseite: teasert das Spiel an, erklärt die Funktionen und führt per Call-to-Action ins Spiel.
 * Rein darstellend (kein Zugriff auf den Spielzustand), damit scripts/prerender.mjs sie beim Build
 * als statisches HTML in die index.html schreiben kann. Suchmaschinen sehen so den Inhalt auch ohne JavaScript.
 */
import { Link } from 'react-router-dom';
import { DIFF_INFO, FAQ, FEATURES, STEPS } from '../content/landing';

export type LandingProps = {
  /** Name der Partei eines laufenden Spielstands (dann „Weiterspielen“ statt „Partei gründen“). */
  resume?: string | null;
  onStart?: () => void;
};

/** Die drei Schwierigkeitsgrade mit ihren Unterschieden. Mit `value`/`onPick` als Auswahl. */
export function DiffCards({ value, onPick }: { value?: string; onPick?: (k: string) => void }) {
  return (
    <div className="lp-diffs">{DIFF_INFO.map((d) => {
      const body = <><span className="label">{d.tag}</span><b className="lp-dn">{d.n}</b>
        <ul>{d.pts.map((p) => <li key={p}>{p}</li>)}</ul></>;
      return onPick
        ? <button type="button" key={d.k} className="lp-diff tcard" aria-pressed={value === d.k} onClick={() => onPick(d.k)}>{body}{value === d.k && <span className="lp-sel">Ausgewählt</span>}</button>
        : <div key={d.k} className="lp-diff">{body}</div>;
    })}</div>
  );
}

function Cta({ resume, onStart, big }: LandingProps & { big?: boolean }) {
  return (
    <div className="lp-cta">
      <Link to="/spiel" className={'btn yes' + (big ? ' big' : '')} onClick={onStart}>{resume ? `Weiterspielen mit ${resume}` : 'Partei gründen und loslegen'}</Link>
      <Link to="/mitspielen" className="btn">Zu zweit spielen</Link>
    </div>
  );
}

export function Landing(props: LandingProps) {
  return (
    <div className="landing">
      <section className="lp-hero" aria-labelledby="lp-title">
        <img className="lp-heroimg" src="/img/szene/regierung.webp" alt="" width={1376} height={768} {...{ fetchpriority: 'high' }} />
        <div className="lp-herotext">
          <span className="label">Die Politiksimulation im Browser</span>
          <h1 id="lp-title">Gewinn die Wahl. Bilde eine Regierung. Setz dich durch.</h1>
          <p className="lp-lead">Gründe eine Partei, führe Wahlkampf und schmiede eine Koalition. Dann beginnt die eigentliche Arbeit: Gesetze durch Bundestag und Bundesrat bringen, den Haushalt aufstellen, Krisen meistern und nach vier Jahren die Wiederwahl schaffen.</p>
          <Cta {...props} big />
          <ul className="lp-facts"><li>Allein oder zu zweit</li><li>Im Browser, ohne Installation</li><li>Spielstand wird automatisch gespeichert</li></ul>
        </div>
      </section>

      <section className="lp-sec" aria-labelledby="lp-steps">
        <div className="lp-head"><span className="label">So läuft eine Partie</span><h2 id="lp-steps">Vom Parteiprogramm ins Kanzleramt</h2></div>
        <ol className="lp-steps">{STEPS.map((s, i) => (
          <li key={s.t} className="lp-step">
            <img src={s.img} alt="" width={1376} height={768} loading="lazy" decoding="async" />
            <div><span className="lp-num">{i + 1}</span><h3>{s.t}</h3><p>{s.d}</p></div>
          </li>))}</ol>
      </section>

      <section className="lp-sec" aria-labelledby="lp-features">
        <div className="lp-head"><span className="label">Was dich erwartet</span><h2 id="lp-features">Regieren ist mehr als Abstimmen</h2>
          <p>Kabinett bildet die Abläufe der deutschen Politik nach. Jede Entscheidung hat Gewinner und Verlierer, und alles hängt zusammen: Geld, Mehrheiten, Stimmung im Land.</p></div>
        <ul className="lp-feats">{FEATURES.map((f) => <li key={f.t}><h3>{f.t}</h3><p>{f.d}</p></li>)}</ul>
      </section>

      <section className="lp-sec" aria-labelledby="lp-diff">
        <div className="lp-head"><span className="label">Schwierigkeitsgrade</span><h2 id="lp-diff">Wie schwer soll es sein?</h2>
          <p>Du wählst den Schwierigkeitsgrad nach der Parteigründung und kannst einzelne Einflüsse jederzeit in den Einstellungen an- oder abschalten.</p></div>
        <DiffCards />
      </section>

      <section className="lp-sec" aria-labelledby="lp-faq">
        <div className="lp-head"><span className="label">Fragen und Antworten</span><h2 id="lp-faq">Gut zu wissen</h2></div>
        <div className="lp-faq">{FAQ.map((f) => <details key={f.q}><summary>{f.q}</summary><p>{f.a}</p></details>)}</div>
      </section>

      <section className="lp-final" aria-labelledby="lp-go">
        <img className="lp-heroimg" src="/img/szene/wahlabend.webp" alt="" width={1376} height={768} loading="lazy" decoding="async" />
        <div className="lp-herotext"><h2 id="lp-go">Bereit für den Wahlabend?</h2><p>Die erste Partie dauert so lange, wie du willst. Die Zeit läuft nur weiter, wenn du es sagst.</p><Cta {...props} /></div>
      </section>
    </div>
  );
}
